import type { DeviceAccount } from '@/api/deviceAccounts';
import type { StatusState } from '@/components/ui/statusSemantics';
import { getDeviceWorkspacePath } from '@/navigation/consoleRoutePaths';
import type { DeviceMonitoringStage, OperationsFleetDevice } from '@/types/operations';

export interface DeviceInventoryPrimaryAction {
  href: string;
  label: string;
}

export interface DeviceInventoryStagePresentation {
  label: string;
  state: StatusState;
}

/** The inventory summary tile a device counts in. */
export type DeviceStageSummaryTile = 'detecting' | 'needs_you' | 'not_detecting_yet';

/** How each server stage reads in the inventory; every stage must be listed. */
const stageMeaning: Record<
  DeviceMonitoringStage,
  { state: StatusState; tile: DeviceStageSummaryTile }
> = {
  detecting: { state: 'ready', tile: 'detecting' },
  paused_input_changed: { state: 'blocked', tile: 'needs_you' },
  waiting_for_approval: { state: 'blocked', tile: 'needs_you' },
  approved_not_started: { state: 'blocked', tile: 'needs_you' },
  setup_blocked: { state: 'failed', tile: 'needs_you' },
  paused: { state: 'blocked', tile: 'needs_you' },
  setting_up: { state: 'loading', tile: 'not_detecting_yet' },
  not_set_up: { state: 'zero', tile: 'not_detecting_yet' },
};

/** The status look of a server stage. */
export function monitoringStageState(stage: DeviceMonitoringStage): StatusState {
  return stageMeaning[stage].state;
}

/** Device counts per summary tile, from the server's per-stage counts. */
export function stageSummaryCounts(
  stages: Record<DeviceMonitoringStage, number>,
): Record<DeviceStageSummaryTile, number> {
  const totals: Record<DeviceStageSummaryTile, number> = {
    detecting: 0,
    needs_you: 0,
    not_detecting_yet: 0,
  };
  for (const [stage, meaning] of Object.entries(stageMeaning)) {
    totals[meaning.tile] += stages[stage as DeviceMonitoringStage];
  }
  return totals;
}

/** The shared server stage, or an honest "unavailable" when it can't be read. */
export function stagePresentationFor(
  account: DeviceAccount,
  record: OperationsFleetDevice | undefined,
): DeviceInventoryStagePresentation {
  if (!account.device_state) return { label: 'No device bound', state: 'failed' };
  if (!record) return { label: 'State unavailable', state: 'stale' };
  return { label: record.monitoring.label, state: monitoringStageState(record.monitoring.stage) };
}

/** The row's one action: the same next step Overview and the device page show. */
export function primaryActionFor(
  account: DeviceAccount,
  record: OperationsFleetDevice | undefined,
): DeviceInventoryPrimaryAction | null {
  const device = account.device_state;
  if (!device) return null;
  if (record) {
    return { href: record.next_action.href, label: record.next_action.label };
  }
  return { href: getDeviceWorkspacePath(device.id), label: 'Open device' };
}
